import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Input, Row, Col, Select, Spin, Typography, Flex,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FormHeaderLabel from '../../../components/Form/HeaderLabel';
import ThaiAddressFields from '../../../components/Form/ThaiAddressFields';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { PathBuilder } from '../../../utils/pathBuilder';

type AlumniFormT = {
  alumniStatus: string;
  userType: string;
  faculty: string;
  graduationYear: number | undefined;
  batch: number | undefined;
  studentId: string;
  preferredName: string;
  jobTitle: string;
  companyName: string;
  companyNameTh: string;
  website: string;
  taxId: string;
  mapUrl: string;
  industry: string;
  homeAddress: string;
  homeAddressLine: string;
  homeSubDistrict: string;
  homeDistrict: string;
  homeProvince: string;
  homePostalCode: string;
  workAddress: string;
  workAddressLine: string;
  workSubDistrict: string;
  workDistrict: string;
  workProvince: string;
  workPostalCode: string;
};

const EditAlumni: React.FC = () => {
  const navigate = useNavigate();
  const { id: customerId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isEmployer, setIsEmployer] = useState(false);
  const pageTitle = isEmployer ? 'Edit employer details' : 'Edit alumni details';

  const { service: getCustomerDetailService } = useServices(ServiceNameEnum.customerMgmt.detail);
  const { service: updateCustomerProfileService } = useServices(ServiceNameEnum.customerMgmt.update);
  const { service: approveAlumniService } = useServices(ServiceNameEnum.customerMgmt.approveAlumni);
  const { service: rejectAlumniService } = useServices(ServiceNameEnum.customerMgmt.rejectAlumni);

  const {
    control, setValue, handleSubmit, watch,
  } = useForm<AlumniFormT>({
    defaultValues: {
      alumniStatus: 'pending',
      userType: 'alumni',
    },
  });

  const homeProvince = watch('homeProvince');
  const homeDistrict = watch('homeDistrict');
  const homeSubDistrict = watch('homeSubDistrict');
  const workProvince = watch('workProvince');
  const workDistrict = watch('workDistrict');
  const workSubDistrict = watch('workSubDistrict');

  const path: PathT[] = [
    {
      label: 'Customer Account',
      value: '/customer',
    },
    {
      label: `Account : ${customerEmail}`,
      value: `/customer/detail/${customerId}`,
    },
    {
      label: pageTitle,
    },
  ];

  const loadCustomerDetail = async (): Promise<void> => {
    if (customerId) {
      const { data, error } = await getCustomerDetailService.get({
        params: { id: customerId },
      });

      if (data) {
        setCustomerEmail(data.email || '');
        setIsEmployer((data.userType || 'alumni') === 'employer');
        setValue('alumniStatus', data.alumniStatus || 'pending');
        setValue('userType', data.userType || 'alumni');
        setValue('faculty', data.faculty || '');
        setValue('graduationYear', data.graduationYear || undefined);
        setValue('batch', data.batch || undefined);
        setValue('studentId', data.studentId || '');
        setValue('preferredName', data.preferredName || '');
        setValue('jobTitle', data.jobTitle || '');
        setValue('companyName', data.companyName || '');
        setValue('companyNameTh', data.companyNameTh || '');
        setValue('website', data.website || '');
        setValue('taxId', data.taxId || '');
        setValue('mapUrl', data.mapUrl || '');
        setValue('industry', data.industry || '');
        setValue('homeAddress', data.homeAddress || '');
        setValue('homeAddressLine', data.homeAddressLine || '');
        setValue('homeSubDistrict', data.homeSubDistrict || '');
        setValue('homeDistrict', data.homeDistrict || '');
        setValue('homeProvince', data.homeProvince || '');
        setValue('homePostalCode', data.homePostalCode || '');
        setValue('workAddress', data.workAddress || '');
        setValue('workAddressLine', data.workAddressLine || '');
        setValue('workSubDistrict', data.workSubDistrict || '');
        setValue('workDistrict', data.workDistrict || '');
        setValue('workProvince', data.workProvince || '');
        setValue('workPostalCode', data.workPostalCode || '');
        setIsLoading(false);
      } else if (error) {
        const errorResponse = AxiosService.getErrorResponse(error);
        toast.error(errorResponse?.message, { toastId: 'allToast' });
      }
    }
  };

  const onFormSubmit = async (formData: AlumniFormT): Promise<void> => {
    setIsLoading(true);

    // Update alumni approval status
    if (formData.alumniStatus === 'approved') {
      const result = await approveAlumniService.post({}, { params: { id: customerId } });
      if (result.error) {
        setIsLoading(false);
        const errorResponse = AxiosService.getErrorResponse(result.error);
        toast.error(errorResponse?.message || 'Failed to approve alumni');
        return;
      }
    } else if (formData.alumniStatus === 'rejected') {
      const result = await rejectAlumniService.post({}, { params: { id: customerId } });
      if (result.error) {
        setIsLoading(false);
        const errorResponse = AxiosService.getErrorResponse(result.error);
        toast.error(errorResponse?.message || 'Failed to reject alumni');
        return;
      }
    }

    const { data, error } = await updateCustomerProfileService.put({
      userType: formData.userType || 'alumni',
      faculty: formData.faculty || null,
      graduationYear: formData.graduationYear ? Number(formData.graduationYear) : null,
      batch: formData.batch ? Number(formData.batch) : null,
      studentId: formData.studentId || null,
      preferredName: formData.preferredName || null,
      jobTitle: formData.jobTitle || null,
      companyName: formData.companyName || null,
      companyNameTh: formData.companyNameTh || null,
      website: formData.website || null,
      taxId: formData.taxId || null,
      mapUrl: formData.mapUrl || null,
      industry: formData.industry || null,
      homeAddress: formData.homeAddress || null,
      homeAddressLine: formData.homeAddressLine || null,
      homeSubDistrict: formData.homeSubDistrict || null,
      homeDistrict: formData.homeDistrict || null,
      homeProvince: formData.homeProvince || null,
      homePostalCode: formData.homePostalCode || null,
      workAddress: formData.workAddress || null,
      workAddressLine: formData.workAddressLine || null,
      workSubDistrict: formData.workSubDistrict || null,
      workDistrict: formData.workDistrict || null,
      workProvince: formData.workProvince || null,
      workPostalCode: formData.workPostalCode || null,
    }, {
      params: { id: customerId },
    });

    setIsLoading(false);
    if (data) {
      toast.success('Updated success');
      navigate(PathBuilder.buildPath(RouteNameEnum.customer.detail, { params: { id: customerId } }));
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message);
    }
  };

  useEffect(() => {
    loadCustomerDetail();
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh',
      }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];
  const userTypeOptions = [
    { value: 'alumni', label: 'Alumni' },
    { value: 'bba_student', label: 'BBA Student' },
  ];

  return (
    <>
      <AlumlyBreadCrumb path={path} />
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography.Title level={4} style={{ fontWeight: 700, margin: 0 }}>{pageTitle}</Typography.Title>
          <div style={{ marginTop: 40, paddingBottom: '32px' }}>
            <FormHeaderLabel label="Alumni status" />
            <Row gutter={[32, 32]} style={{ marginTop: 8 }}>
              <Col sm={12} xs={24}>
                <Typography.Text type="secondary">Approval status</Typography.Text>
                <Controller
                  name="alumniStatus"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="large"
                      style={{ width: '100%' }}
                      value={field.value || 'pending'}
                      onChange={field.onChange}
                      options={statusOptions}
                    />
                  )}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 32 }} />
            <FormHeaderLabel label={isEmployer ? 'Employer info' : 'Alumni info'} />
            <Row gutter={[32, 32]} style={{ marginTop: 8 }}>
              {isEmployer ? (
                <>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Company name (EN)</Typography.Text>
                    <Controller
                      name="companyName"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Company name (EN)" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Company name (TH)</Typography.Text>
                    <Controller
                      name="companyNameTh"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Company name (TH)" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Company / Tax ID</Typography.Text>
                    <Controller
                      name="taxId"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Company / Tax ID" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Industry</Typography.Text>
                    <Controller
                      name="industry"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Industry" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Website / LinkedIn</Typography.Text>
                    <Controller
                      name="website"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Website / LinkedIn" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={24} xs={24}>
                    <Typography.Text type="secondary">Company Google Maps URL</Typography.Text>
                    <Controller
                      name="mapUrl"
                      control={control}
                      render={({ field }) => (
                        <Input
                          size="large"
                          placeholder="https://maps.google.com/..."
                          {...field}
                        />
                      )}
                    />
                  </Col>
                </>
              ) : (
                <>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">User type</Typography.Text>
                    <Controller
                      name="userType"
                      control={control}
                      render={({ field }) => (
                        <Select
                          size="large"
                          style={{ width: '100%' }}
                          value={field.value || 'alumni'}
                          onChange={field.onChange}
                          options={userTypeOptions}
                        />
                      )}
                    />
                  </Col>
                  {/* Faculty field hidden (คณะ — not collected in current flow)
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Faculty</Typography.Text>
                    <Controller
                      name="faculty"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Faculty" {...field} />
                      )}
                    />
                  </Col>
                  */}
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">SHI</Typography.Text>
                    <Controller
                      name="graduationYear"
                      control={control}
                      render={({ field }) => (
                        <Input
                          size="large"
                          type="number"
                          placeholder="e.g. 2540"
                          {...field}
                        />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">รุ่นที่ BBA</Typography.Text>
                    <Controller
                      name="batch"
                      control={control}
                      render={({ field }) => (
                        <Input
                          size="large"
                          type="number"
                          placeholder="e.g. 13"
                          {...field}
                        />
                      )}
                    />
                  </Col>
                  {/* Student ID field hidden (รหัสนักศึกษา — not collected in current flow)
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Student ID</Typography.Text>
                    <Controller
                      name="studentId"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Student ID" {...field} />
                      )}
                    />
                  </Col>
                  */}
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Preferred name</Typography.Text>
                    <Controller
                      name="preferredName"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Preferred name" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Job title</Typography.Text>
                    <Controller
                      name="jobTitle"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Job title" {...field} />
                      )}
                    />
                  </Col>
                  <Col sm={12} xs={24}>
                    <Typography.Text type="secondary">Company</Typography.Text>
                    <Controller
                      name="companyName"
                      control={control}
                      render={({ field }) => (
                        <Input size="large" placeholder="Company name" {...field} />
                      )}
                    />
                  </Col>
                </>
              )}
            </Row>

            {!isEmployer && (
              <>
                <div style={{ marginTop: 32 }} />
                <FormHeaderLabel label="Home address" />
                <ThaiAddressFields
                  prefix="home"
                  control={control}
                  setValue={setValue}
                  watchProvince={homeProvince}
                  watchDistrict={homeDistrict}
                  watchSubDistrict={homeSubDistrict}
                />
              </>
            )}

            <div style={{ marginTop: 32 }} />
            <FormHeaderLabel label="Work address" />
            <ThaiAddressFields
              prefix="work"
              control={control}
              setValue={setValue}
              watchProvince={workProvince}
              watchDistrict={workDistrict}
              watchSubDistrict={workSubDistrict}
            />
            <Flex
              gap={32}
              justify="center"
              vertical={false}
              wrap="wrap"
              style={{ marginTop: 32 }}
            >
              <AlumlyButton
                label="rxvWhite"
                width={150}
                icon={<CloseIcon />}
                onClick={
                  () => navigate(PathBuilder.buildPath(
                    RouteNameEnum.customer.detail,
                    { params: { id: customerId } },
                  ))
                }
              >
                Cancel
              </AlumlyButton>
              <AlumlyButton
                label="rxv"
                width={150}
                type="submit"
                icon={<CheckIcon />}
              >
                Confirm
              </AlumlyButton>
            </Flex>
          </div>
        </div>
      </form>
    </>
  );
};

export default EditAlumni;
